import React from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { combineUrl, memoizeFunction, toCamelCasedKeys } from '@agilepoint/utils-common';
import { Icon, StackPanel, Typography } from '@agilepoint/wat-react-ux';
import { CheckboxField, TextBoxField } from '@agilepoint/wat-react-ux-form-fields';
import { ILoginPageFormFields } from '../../types';
import { CenteredSpinner, StyledPrimaryButton } from 'common';
import { useLoadConfig, useSetWindowObjConfig } from 'modules/login/hooks';

interface ILoginControlsProps {

}

const Heading = styled(Typography.H1)`
    font-style: normal;
    font-weight: 700;
    font-size: 30px;
    line-height: 42px;
    text-align: left;
    padding-bottom: 40px;
`;
const SignInButton = styled(StyledPrimaryButton)`
    margin-right:11px;
`;

const StyledStackPanel = styled(StackPanel)`
    padding: 12px 24px 12px 16px;
`;

const StyledTextBoxField = styled(TextBoxField) <{ showErrorAnimation: boolean }>`
    .ap-textbox-wrapper {
        width: 338px;
        height: 48px;
        padding:0px;
        border-color: #CACED5;
        border-radius: 6px;
        .ap-textbox{
            padding:0 16px !important;
        }
    }
    .ap-formField-label span {
        font-size: 12px;
        line-height: 18px;
        color: ${({ theme }) => theme.mobileAppAccelerator.semanticColors.grayVariant2};
    }
    @keyframes nodStyle {
        0% {
            transform: translateX(0);
        }
        6.5% {
            transform: translateX(-6px) rotateY(-9deg);
        }
        18.5% {
            transform: translateX(5px) rotateY(7deg);
        }
        31.5% {
            transform: translateX(-3px) rotateY(-5deg);
        }
        43.5% {
            transform: translateX(2px) rotateY(3deg);
        }
        50% {
            transform: translateX(0);
        }
    }
    animation: ${props => (props.showErrorAnimation ? 'nodStyle 3s' : 'none')};

`;

const LoginControlsBaseWrapper = styled(StackPanel)`
    margin: auto;
`;

const ActionButtonsWrapper = styled(StackPanel)`
    margin-top: 28px;
`;

const authPrefix = 'nxone\\';

const StyledCheckboxField = styled(CheckboxField)`
    .ap-checkbox-label {
        font-size: 14px;
    }
`;

const StyledForm = styled.form`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const StyledWarningMessage = styled.div`
    color: #B6383E;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    padding-bottom: 2px;
`;

const RelativeDiv = styled.div`
    position: relative;
`;

const ShowPasswordIcon = styled(Icon) <{ $isError?: string }>`
    position: absolute;
    right: 16px;
    bottom: ${({ $isError }) => ($isError ? '34px' : '16px')};
    fill: #444444;
    cursor: pointer;
`;

// const ForgotPassword = styled(Typography.H6)`
//     color: ${({ theme }) => theme.semantics.palette.themePrimary};
//     cursor: pointer;
// `;

const LoginControlsBase = React.memo(() => {
    const [showError, setShowError] = React.useState(false);
    const [isLoginLoading, setIsLoginLoading] = React.useState(false);
    const { defaultConfig, isLoading } = useLoadConfig();
    const [showPassword, setShowPassword] = React.useState(false);
    const location = useLocation();
    const state = location.state as any;

    const { setUserDetails } = useSetWindowObjConfig({
        endPointURL: defaultConfig.apiEndPoint,
        basePath: defaultConfig.basePath,
        baseURL: defaultConfig.baseUrl,
        cookieExpirationValue: defaultConfig?.mobileAppAccelerator?.cookieExpirationValue
    });

    const onLogin = React.useCallback(async (args: ILoginPageFormFields) => {
        const { username, password, keepMeSignedIn } = args;
        const payload = getAuthenticateUserPayload(username, password);
        setIsLoginLoading(true);
        await fetch(`${defaultConfig.apiEndPoint}Authenticate`, {
            headers: payload,
            method: 'GET'
        }).then((response) => {
            if (response.ok) {
                return response.json();
            }
            setShowError(true);
            setIsLoginLoading(false);
            throw new Error(`Error: ${response.status} - ${response.statusText}`);
        }).then((r) => {
            setUserDetails(toCamelCasedKeys(r.AuthenticateResult), payload.Authorization, keepMeSignedIn);
            setIsLoginLoading(false);
            if (state?.previousCompletePath) {
                return window.open(combineUrl(defaultConfig.baseUrl, state.previousCompletePath), '_self');
            }
            return window.open(defaultConfig.baseUrl, '_self');
        }).catch((err) => {
            console.error('error', err);
            setShowError(true);
            setIsLoginLoading(false);
        });
    }, [defaultConfig.apiEndPoint, defaultConfig.baseUrl, setUserDetails, state]);

    const { handleSubmit, formState } = useFormContext<ILoginPageFormFields>();

    const onShowPasswordClick = React.useCallback(() => (setShowPassword((prevValue) => !prevValue)), []);

    // const navigateToResetPassword = React.useCallback(() => {
    //     navigate('/reset-password');
    // }, [navigate]);

    if (isLoading) {
        return (<CenteredSpinner spinnerType="determinate" />);
    }

    return (
        <LoginControlsBaseWrapper orientation="vertical" justifyContent="center" width="350px">
            <Heading>
                Sign In to AgilePoint
                Mobile App Accelerator
            </Heading>
            {showError ?
                <StyledWarningMessage>Username or Password is incorrect.</StyledWarningMessage> : <></>}
            <StackPanel orientation="vertical" gap='23'>
                <StyledTextBoxField name="username" type="text" label="USER NAME"
                    validationRules={{ required: 'Please enter user name' }} showErrorAnimation={showError} />
                <RelativeDiv>
                    <StyledTextBoxField name="password" type={showPassword ? 'text' : 'password'} label="PASSWORD"
                        validationRules={{ required: 'Please enter password' }} showErrorAnimation={showError} />
                    <ShowPasswordIcon
                        $isError={formState.errors.password?.message}
                        iconName={showPassword ? 'ap-eye.solid' : 'ap-hide-preview.solid'}
                        onClick={onShowPasswordClick} />
                </RelativeDiv>
            </StackPanel>
            <ActionButtonsWrapper width="100%" orientation="horizontal" justifyContent="space-between">
                <StyledCheckboxField label="Keep me signed in" name="keepMeSignedIn" />
                {isLoginLoading ? <CenteredSpinner spinnerType='indeterminate' size="small" width='125px' /> :
                    <SignInButton label="Sign In" onClick={handleSubmit(onLogin)} />}
            </ActionButtonsWrapper>
            {/* <ForgotPassword title={'Forgot Password?'} tabIndex={0}
                onClick={navigateToResetPassword}>Forgot Password?</ForgotPassword> */}
        </LoginControlsBaseWrapper>
    );
});

export function LoginControls(props: ILoginControlsProps) {

    const methods = useForm({
        mode: 'onBlur'
    });

    return (
        <StyledStackPanel orientation="vertical" width="calc(100% - 443px)" height="100%" justifyContent="center" alignContent="center">
            <FormProvider {...methods}>
                <StyledForm>
                    <LoginControlsBase />
                </StyledForm>
            </FormProvider>
        </StyledStackPanel>
    );
}

const getAuthenticateUserPayload = memoizeFunction((userName: string, password: string) => ({
    'Authorization': ('Basic ' + window.btoa(`${authPrefix}` + userName + ':' + password) + '')
}));
